// Blueprints — saved, ordered stacks of blocks used as publishing scaffolds.
// Wave 2 persistence: composer_blueprints + composer_blueprint_block_refs.

import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import {
  BLOCK_TYPE_LABELS,
  SEED_BLUEPRINTS,
  type ComposerBlock,
  type ComposerBlockType,
  type ComposerBlueprint,
  type ComposerBlueprintBlockRef,
} from "./types";

export const resolveBlueprintBlockRef = (
  ref: ComposerBlueprintBlockRef,
  blocks: ComposerBlock[],
): ComposerBlock | null => {
  if (ref.blockId) return blocks.find((b) => b.id === ref.blockId) ?? null;
  // Fall back to the first block of that type in the workspace.
  return blocks.find((b) => b.type === ref.type) ?? null;
};

export const blueprintToPlainText = (
  blueprint: ComposerBlueprint,
  blocks: ComposerBlock[],
): string => {
  return blueprint.blockRefs
    .map((ref) => {
      const block = resolveBlueprintBlockRef(ref, blocks);
      if (block) return block.body;
      return `[${BLOCK_TYPE_LABELS[ref.type]}${ref.note ? ` — ${ref.note}` : ""}]`;
    })
    .join("\n\n");
};

type BlueprintRow = Tables<"composer_blueprints">;
type BlueprintRefRow = Tables<"composer_blueprint_block_refs">;

const refRowToRef = (r: BlueprintRefRow): ComposerBlueprintBlockRef => ({
  type: r.block_type as ComposerBlockType,
  blockId: r.block_id ?? undefined,
  note: r.note ?? undefined,
});

const rowToBlueprint = (
  row: BlueprintRow,
  refs: BlueprintRefRow[],
): ComposerBlueprint => ({
  id: row.id,
  title: row.title,
  description: row.description ?? "",
  contentTypeHint: row.content_type_hint ?? undefined,
  blockRefs: refs
    .filter((r) => r.blueprint_id === row.id)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(refRowToRef),
});

export const listBlueprints = async (workspaceId: string): Promise<ComposerBlueprint[]> => {
  const { data: bps, error } = await supabase
    .from("composer_blueprints")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  const ids = (bps ?? []).map((b) => b.id);
  if (ids.length === 0) return [];
  const { data: refs, error: refsErr } = await supabase
    .from("composer_blueprint_block_refs")
    .select("*")
    .in("blueprint_id", ids);
  if (refsErr) throw new Error(refsErr.message);
  return (bps ?? []).map((b) => rowToBlueprint(b, refs ?? []));
};

const writeRefs = async (
  blueprintId: string,
  blockRefs: ComposerBlueprintBlockRef[],
): Promise<void> => {
  const { error: delErr } = await supabase
    .from("composer_blueprint_block_refs")
    .delete()
    .eq("blueprint_id", blueprintId);
  if (delErr) throw new Error(delErr.message);
  if (blockRefs.length === 0) return;
  const rows: TablesInsert<"composer_blueprint_block_refs">[] = blockRefs.map((r, i) => ({
    blueprint_id: blueprintId,
    block_type: r.type,
    block_id: r.blockId ?? null,
    note: r.note ?? null,
    sort_order: i,
  }));
  const { error } = await supabase.from("composer_blueprint_block_refs").insert(rows);
  if (error) throw new Error(error.message);
};

export const createBlueprint = async (
  workspaceId: string,
  values: {
    title: string;
    description?: string;
    contentTypeHint?: string;
    blockRefs: ComposerBlueprintBlockRef[];
  },
): Promise<ComposerBlueprint> => {
  const { data: u } = await supabase.auth.getUser();
  const payload: TablesInsert<"composer_blueprints"> = {
    workspace_id: workspaceId,
    title: values.title,
    description: values.description ?? "",
    content_type_hint: values.contentTypeHint ?? null,
    created_by_user_id: u.user?.id ?? null,
  };
  const { data, error } = await supabase
    .from("composer_blueprints")
    .insert(payload)
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  await writeRefs(data.id, values.blockRefs);
  return rowToBlueprint(data, []);
};

export const updateBlueprint = async (
  id: string,
  values: {
    title?: string;
    description?: string;
    contentTypeHint?: string | null;
    blockRefs?: ComposerBlueprintBlockRef[];
  },
): Promise<void> => {
  const patch: TablesUpdate<"composer_blueprints"> = {};
  if (values.title !== undefined) patch.title = values.title;
  if (values.description !== undefined) patch.description = values.description;
  if (values.contentTypeHint !== undefined) patch.content_type_hint = values.contentTypeHint;
  if (Object.keys(patch).length > 0) {
    const { error } = await supabase.from("composer_blueprints").update(patch).eq("id", id);
    if (error) throw new Error(error.message);
  }
  if (values.blockRefs !== undefined) await writeRefs(id, values.blockRefs);
};

export const deleteBlueprint = async (id: string): Promise<void> => {
  const { error } = await supabase.from("composer_blueprints").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

/** Duplicate a blueprint (with its block refs) as a new "(copy)" row. */
export const duplicateBlueprint = async (
  workspaceId: string,
  bp: ComposerBlueprint,
): Promise<ComposerBlueprint> => {
  return createBlueprint(workspaceId, {
    title: `${bp.title} (copy)`,
    description: bp.description,
    contentTypeHint: bp.contentTypeHint,
    blockRefs: bp.blockRefs.map((r) => ({ ...r })),
  });
};

/**
 * "How many blueprints reference each block?" map, computed from the workspace's
 * persisted block refs. Returns a Map<blockId, count>.
 */
export const countBlockUsageInBlueprints = async (
  workspaceId: string,
): Promise<Map<string, number>> => {
  const { data: bps } = await supabase
    .from("composer_blueprints")
    .select("id")
    .eq("workspace_id", workspaceId);
  const ids = (bps ?? []).map((b) => b.id);
  if (ids.length === 0) return new Map();
  const { data: refs } = await supabase
    .from("composer_blueprint_block_refs")
    .select("block_id")
    .in("blueprint_id", ids);
  const counts = new Map<string, number>();
  for (const r of (refs ?? []) as Array<{ block_id: string | null }>) {
    if (!r.block_id) continue;
    counts.set(r.block_id, (counts.get(r.block_id) ?? 0) + 1);
  }
  return counts;
};

/**
 * Lazily seed a workspace with SEED_BLUEPRINTS on first panel open.
 * Idempotent: skips slugs already present in metadata.seed_slug.
 */
export const ensureSeedBlueprints = async (workspaceId: string): Promise<void> => {
  const { data: existing, error: selErr } = await supabase
    .from("composer_blueprints")
    .select("metadata")
    .eq("workspace_id", workspaceId);
  if (selErr) throw new Error(selErr.message);
  const present = new Set<string>();
  for (const r of existing ?? []) {
    const md = (r.metadata as Record<string, unknown> | null) ?? null;
    const slug = md && typeof md.seed_slug === "string" ? (md.seed_slug as string) : null;
    if (slug) present.add(slug);
  }
  const missing = SEED_BLUEPRINTS.filter((s) => !present.has(s.id));
  if (missing.length === 0) return;
  const { data: u } = await supabase.auth.getUser();
  for (let i = 0; i < missing.length; i++) {
    const seed = missing[i];
    const { data: bp, error } = await supabase
      .from("composer_blueprints")
      .insert({
        workspace_id: workspaceId,
        title: seed.title,
        description: seed.description,
        content_type_hint: seed.contentTypeHint ?? null,
        sort_order: i,
        created_by_user_id: u.user?.id ?? null,
        metadata: { seed_slug: seed.id },
      })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    await writeRefs(bp.id, seed.blockRefs);
  }
};
